import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { 
  Calendar as CalendarIcon, 
  Download, 
  Car, 
  ArrowUpRight, 
  ArrowDownLeft, 
  Clock, 
  Activity, 
  CheckCircle2,
  AlertTriangle,
  ShieldX,
  Gauge
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { toast } from "sonner";

const API = '/api';

// =============================================================================
// OPERATIONAL MOVEMENT GROUPS
// =============================================================================
// These groups represent how the car park physically operates, not camera identity.
// Events are classified by direction + camera type + decision outcome.
// =============================================================================

const isComboCamera = (evt) => {
  const camera = (evt.camera_id || "").toLowerCase();
  const barrier = (evt.barrier_id || "").toLowerCase();
  // Match combo by name or known combo camera IDs
  return camera.includes("combo") || 
         barrier.includes("combo") || 
         camera.includes("235d49"); // Known combo camera logical ID
};

const isBlockedDecision = (decision) => {
  const d = (decision || "").toUpperCase().trim();
  return d === "BLOCKED" || d === "DENY" || d === "DENIED";
};

const OPERATIONAL_GROUPS = [
  // 1. RAMP ENTRY - Dedicated entry camera, entry direction
  { 
    id: "ramp-entry", 
    label: "Ramp Entry",
    description: "Vehicles entering via the dedicated ramp entry camera",
    color: "bg-green-50 border-green-300",
    headerColor: "bg-green-600 text-white",
    icon: ArrowDownLeft,
    match: (evt) => {
      const direction = (evt.direction || "").toLowerCase();
      if (direction !== "entry") return false;
      if (isComboCamera(evt)) return false;
      return true;
    }
  },
  // 2. RAMP EXIT - Dedicated exit camera, exit direction, genuine exits
  { 
    id: "ramp-exit", 
    label: "Ramp Exit",
    description: "Vehicles exiting via the dedicated ramp exit camera",
    color: "bg-orange-50 border-orange-300",
    headerColor: "bg-orange-600 text-white",
    icon: ArrowUpRight,
    match: (evt) => {
      const direction = (evt.direction || "").toLowerCase();
      if (direction !== "exit") return false;
      if (isComboCamera(evt)) return false;
      return true;
    }
  },
  // 3. COMBO ENTRY - Combo camera, entry direction, ALLOW only
  { 
    id: "combo-entry", 
    label: "Combo Entry",
    description: "Vehicles entering detected by the combo camera",
    color: "bg-blue-50 border-blue-300",
    headerColor: "bg-blue-600 text-white",
    icon: ArrowDownLeft,
    match: (evt) => {
      const direction = (evt.direction || "").toLowerCase();
      if (direction !== "entry") return false;
      if (!isComboCamera(evt)) return false;
      // Combo entry should be ALLOW - exclude blocked/exit decisions
      if (isBlockedDecision(evt.decision)) return false;
      return true;
    }
  },
  // 4. COMBO EXIT - Combo camera, exit direction, genuine exits (NOT blocked)
  { 
    id: "combo-exit", 
    label: "Combo Exit",
    description: "Vehicles exiting processed by the combo camera",
    color: "bg-purple-50 border-purple-300",
    headerColor: "bg-purple-600 text-white",
    icon: ArrowUpRight,
    match: (evt) => {
      const direction = (evt.direction || "").toLowerCase();
      if (direction !== "exit") return false;
      if (!isComboCamera(evt)) return false;
      // Genuine exit - NOT blocked
      if (isBlockedDecision(evt.decision)) return false;
      return true;
    }
  },
  // 5. COMBO EXIT BLOCKED - Combo camera, exit direction, BLOCKED only
  { 
    id: "combo-blocked", 
    label: "Combo Exit Blocked",
    description: "Mirror reads blocked by 25-second anti-retrigger protection",
    color: "bg-slate-100 border-slate-400",
    headerColor: "bg-slate-700 text-white",
    icon: ShieldX,
    match: (evt) => {
      const direction = (evt.direction || "").toLowerCase();
      if (direction !== "exit") return false;
      if (!isComboCamera(evt)) return false;
      // Only blocked decisions
      if (!isBlockedDecision(evt.decision)) return false;
      return true;
    }
  },
  // 6. UNKNOWN / UNMAPPED - Genuine fallback only
  { 
    id: "unknown", 
    label: "Unknown / Unmapped",
    description: "Events that could not be classified into operational groups",
    color: "bg-red-50 border-red-300",
    headerColor: "bg-red-600 text-white",
    icon: AlertTriangle,
    match: () => true // Fallback - catches anything not matched above
  }
];

// =============================================================================
// DECISION DISPLAY TERMINOLOGY
// =============================================================================
// Operational labels for control room display
// Labels designed for operations managers, not developers
// =============================================================================

const normalizeDecisionForDisplay = (decision, direction, exitReason = null, isComboBlocked = false) => {
  if (!decision) return "Unknown";
  
  const upperDecision = (decision || "").toUpperCase().trim();
  
  // COMBO EXIT BLOCKED section - special label
  if (isComboBlocked) {
    return "Blocked (25s Mirror)";
  }
  
  // FREE_EXIT with whitelist/freeflow exit_reason = Whitelisted Free Exit
  if (upperDecision === "FREE_EXIT" || upperDecision === "FREE") {
    if (exitReason) {
      const upperReason = exitReason.toUpperCase().trim();
      if (["WHITELIST", "WHITELISTED", "FREEFLOW", "PERMIT", "STAFF", "NHS", 
           "BLUE_BADGE", "DISABLED", "RESIDENT", "SEASON_TICKET"].includes(upperReason)) {
        return "Whitelisted Free Exit";
      }
    }
    // FREE_EXIT without identifiable exit_reason = generic Free Exit
    return "Free Exit";
  }
  
  // Map decisions to operational terminology
  const decisionMap = {
    // Entry decisions
    "ALLOW": "Allow",
    "ALLOWED": "Allow",
    
    // Exit decisions - paid
    "PAID_EXIT": "Paid Exit",
    "PAID": "Paid Exit",
    
    // Exit decisions - payment required
    "CHARGE": "Payment Required",
    "REQUIRE_PAYMENT": "Payment Required",
    "PAYMENT_REQUIRED": "Payment Required",
    
    // No session - treated as free exit
    "NO_SESSION_FREE_EXIT": "Free Exit",
    "NO_SESSION": "Free Exit",
    
    // Blocked - should only appear in Combo Exit Blocked section
    // but if it appears elsewhere, show generic blocked
    "BLOCKED": "Blocked",
    "DENY": "Blocked",
    "DENIED": "Blocked"
  };
  
  return decisionMap[upperDecision] || upperDecision;
};

const getDecisionBadgeColor = (normalizedDecision) => {
  const colors = {
    // Entry
    "Allow": "bg-green-100 text-green-800 border-green-300",
    
    // Exit - free
    "Free Exit": "bg-green-100 text-green-800 border-green-300",
    "Whitelisted Free Exit": "bg-purple-100 text-purple-800 border-purple-300",
    
    // Exit - paid
    "Paid Exit": "bg-blue-100 text-blue-800 border-blue-300",
    
    // Exit - payment required
    "Payment Required": "bg-yellow-100 text-yellow-800 border-yellow-300",
    
    // Blocked
    "Blocked (25s Mirror)": "bg-slate-200 text-slate-700 border-slate-400",
    "Blocked": "bg-slate-200 text-slate-700 border-slate-400",
    
    // Unknown
    "Unknown": "bg-gray-100 text-gray-800 border-gray-300"
  };
  return colors[normalizedDecision] || "bg-gray-100 text-gray-800 border-gray-300";
};

export default function AdminDailyMovement() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date());
  const [startTime, setStartTime] = useState("00:01");
  const [endTime, setEndTime] = useState("23:59");
  const [reportData, setReportData] = useState(null);
  const [user, setUser] = useState(null);
  const selectedSite = localStorage.getItem("adminSelectedSite") || "all";

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        navigate("/admin/login");
      }
    };
    checkAuth();
  }, [navigate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/reports/daily-vrm-movement`, {
        params: { date: dateStr, start_time: startTime, end_time: endTime, include_events: true, ...(selectedSite !== "all" ? { site_id: selectedSite } : {}) },
        withCredentials: true
      });
      
      if (response.data.error) {
        toast.error(response.data.error);
        return;
      }
      
      setReportData(response.data);
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/reports/daily-vrm-movement`, {
        params: { date: dateStr, start_time: startTime, end_time: endTime, format: "csv", ...(selectedSite !== "all" ? { site_id: selectedSite } : {}) },
        withCredentials: true,
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `vrm_movement_${dateStr}_${startTime.replace(":", "")}-${endTime.replace(":", "")}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Report exported successfully");
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  useEffect(() => {
    if (user) {
      fetchReport();
    }
  }, [user]);

  // Flatten all events from all VRMs
  const getAllEvents = () => {
    if (!reportData?.movements) return [];
    
    const allEvents = [];
    reportData.movements.forEach(movement => {
      if (movement.events && movement.events.length > 0) {
        movement.events.forEach(evt => {
          allEvents.push({
            ...evt,
            vrm: movement.vrm
          });
        });
      }
    });
    
    return allEvents;
  };

  // Group events by operational movement type
  const getGroupedEvents = () => {
    const allEvents = getAllEvents();
    const grouped = {};
    
    // Initialize groups
    OPERATIONAL_GROUPS.forEach(group => {
      grouped[group.id] = [];
    });
    
    // Assign events to operational groups
    allEvents.forEach(evt => {
      let assigned = false;
      for (const group of OPERATIONAL_GROUPS) {
        if (group.id !== "unknown" && group.match(evt)) {
          grouped[group.id].push(evt);
          assigned = true;
          break;
        }
      }
      // Fallback to unknown only if genuinely unclassifiable
      if (!assigned) {
        grouped["unknown"].push(evt);
      }
    });
    
    // Sort each group by timestamp descending (most recent first)
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });
    });
    
    return grouped;
  };

  const formatTime24 = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const groupedEvents = getGroupedEvents();
  const totalEvents = Object.values(groupedEvents).reduce((sum, arr) => sum + arr.length, 0);
  
  // Camera health check - Combo Entry vs Combo Exit Blocked
  const comboEntryCount = groupedEvents["combo-entry"]?.length || 0;
  const comboBlockedCount = groupedEvents["combo-blocked"]?.length || 0;
  const healthDifference = Math.abs(comboEntryCount - comboBlockedCount);
  const isHealthy = healthDifference === 0;

  return (
    <div className="min-h-screen bg-slate-100" data-testid="daily-movement-page">
      {/* Header */}
      <header className="bg-slate-900 text-white px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold">Daily Vehicle Movement</h1>
            <p className="text-slate-400 text-sm mt-1">Operational control room view</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate("/admin/dashboard")} 
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            data-testid="back-to-dashboard"
          >
            Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="p-6 max-w-7xl mx-auto">
        {/* Report Parameters */}
        <Card className="mb-6 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
              <Clock className="h-5 w-5 text-slate-600" />
              Report Parameters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[200px] justify-start text-left font-normal" data-testid="date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(date, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={(d) => d && setDate(d)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Start Time</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="start-time"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">End Time</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                  data-testid="end-time"
                />
              </div>

              <Button onClick={fetchReport} disabled={loading} className="bg-slate-800 hover:bg-slate-700" data-testid="generate-report">
                {loading ? "Loading..." : "Generate Report"}
              </Button>
              
              <Button variant="outline" onClick={exportCSV} disabled={!reportData} data-testid="export-csv">
                <Download className="mr-2 h-4 w-4" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {reportData && (
          <>
            {/* Summary Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="shadow-sm" data-testid="total-events">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Total Movements</p>
                      <p className="text-3xl font-bold text-slate-900">{totalEvents}</p>
                    </div>
                    <Activity className="h-10 w-10 text-slate-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm" data-testid="total-unique-vrms">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Unique Vehicles</p>
                      <p className="text-3xl font-bold text-slate-900">{reportData.totals.unique_vrms}</p>
                    </div>
                    <Car className="h-10 w-10 text-slate-400" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm" data-testid="total-entries">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Entry Movements</p>
                      <p className="text-3xl font-bold text-green-600">{reportData.totals.total_entry_reads}</p>
                    </div>
                    <ArrowDownLeft className="h-10 w-10 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-sm" data-testid="total-exits">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600 font-medium">Exit Movements</p>
                      <p className="text-3xl font-bold text-orange-600">{reportData.totals.total_exit_reads}</p>
                    </div>
                    <ArrowUpRight className="h-10 w-10 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Camera Health Check */}
            <Card className={`mb-6 shadow-sm border-2 ${isHealthy ? 'border-green-300 bg-green-50' : 'border-amber-300 bg-amber-50'}`} data-testid="camera-health">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Gauge className="h-5 w-5" />
                  Combo Camera Health Check
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <p className="text-sm text-slate-600 font-medium">Combo Entry</p>
                    <p className="text-2xl font-bold text-blue-600" data-testid="combo-entry-count">{comboEntryCount}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-600 font-medium">Combo Exit Blocked</p>
                    <p className="text-2xl font-bold text-slate-600" data-testid="combo-blocked-count">{comboBlockedCount}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-600 font-medium">Difference</p>
                    <p className="text-2xl font-bold" data-testid="health-difference">{healthDifference}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    {isHealthy ? (
                      <>
                        <CheckCircle2 className="h-6 w-6 text-green-600" />
                        <span className="text-green-700 font-semibold" data-testid="health-status">Healthy</span>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-6 w-6 text-amber-600" />
                        <span className="text-amber-700 font-semibold" data-testid="health-status">Warning - Counts Differ</span>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Each vehicle entering via Combo Entry should produce a corresponding Combo Exit Blocked read. 
                  A difference may indicate missed camera reads.
                </p>
              </CardContent>
            </Card>

            {/* Operational Movement Groups */}
            <div className="space-y-6">
              {OPERATIONAL_GROUPS.map(group => {
                const events = groupedEvents[group.id] || [];
                const GroupIcon = group.icon;
                
                // Skip unknown if empty
                if (group.id === "unknown" && events.length === 0) {
                  return null;
                }
                
                return (
                  <Card key={group.id} className={`border-2 shadow-sm ${group.color}`} data-testid={`group-${group.id}`}>
                    <CardHeader className={`${group.headerColor} rounded-t-lg py-3`}>
                      <CardTitle className="text-lg flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <GroupIcon className="h-5 w-5" />
                          {group.label}
                        </div>
                        <span className="text-sm font-normal opacity-90">
                          {events.length} movement{events.length !== 1 ? "s" : ""}
                        </span>
                      </CardTitle>
                      <p className="text-sm opacity-80 mt-1">{group.description}</p>
                    </CardHeader>
                    <CardContent className="p-0">
                      {events.length === 0 ? (
                        <div className="text-center py-8 text-slate-400">
                          No movements in this period
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid={`table-${group.id}`}>
                            <thead>
                              <tr className="border-b border-slate-200 bg-white/50">
                                <th className="text-left py-3 px-4 font-semibold text-slate-700 whitespace-nowrap">Time</th>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700">Vehicle</th>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700">Outcome</th>
                                <th className="text-left py-3 px-4 font-semibold text-slate-700 text-xs">Reference</th>
                              </tr>
                            </thead>
                            <tbody>
                              {events.map((evt, idx) => {
                                // Pass isComboBlocked flag for Combo Exit Blocked section
                                const isComboBlocked = group.id === "combo-blocked";
                                const displayDecision = normalizeDecisionForDisplay(
                                  evt.decision, 
                                  evt.direction, 
                                  evt.exit_reason || evt.reason,
                                  isComboBlocked
                                );
                                return (
                                  <tr 
                                    key={evt.event_id || `${group.id}-evt-${idx}`} 
                                    className="border-b border-slate-100 hover:bg-white/70"
                                    data-testid={`${group.id}-row-${idx}`}
                                  >
                                    <td className="py-3 px-4 font-mono text-slate-900 whitespace-nowrap">
                                      {formatTime24(evt.timestamp)}
                                    </td>
                                    <td className="py-3 px-4 font-mono font-bold text-slate-900">
                                      {evt.vrm || "-"}
                                    </td>
                                    <td className="py-3 px-4">
                                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getDecisionBadgeColor(displayDecision)}`}>
                                        {displayDecision}
                                      </span>
                                    </td>
                                    <td className="py-3 px-4 text-slate-400 font-mono text-xs">
                                      {evt.event_id || "-"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
